import React from 'react';
import { ContextMenu } from '@rocco/ui/context-menu/components/ContextMenu';
import type { ContextMenuItem } from '@rocco/ui/context-menu/components/ContextMenu';
import { TileAdminTimestampInfo } from '@rocco/ui/tile-shared/components/TileAdminTimestampInfo';
import type { DirectoryItemKPI } from '@rocco/ui/tile-shared/components/TileKPIContextMenu';
import { TileKPIContextMenu } from '@rocco/ui/tile-shared/components/TileKPIContextMenu';
import { AlertDialog } from '@rocco/ui/dialog/components/AlertDialog';

interface AdminContextMenuProps {
    children: React.ReactNode;
    onEdit?: () => void;
    onRepositionImage?: () => void;
    onTogglePin?: () => void;
    onToggleFeature?: () => void;
    onToggleFeaturedBrandPin?: () => void;
    onRemoveFromLibrary?: () => void;
    onDelete?: () => void;
    statusInfo?: {
        created: {
            createdBy: string;
            createdAt: string;
        };
        approved?: {
            approvedBy: string;
            approvedAt: string;
        };
        published?: {
            publishedBy: string;
            publishedAt: string;
        };
        pinned?: boolean;
        featured?: boolean;
        featuredBrandPinned?: boolean;
        kpi?: DirectoryItemKPI;
    };
}

export const AdminContextMenu: React.FC<AdminContextMenuProps> = ({
    children,
    onEdit,
    onRepositionImage,
    onDelete,
    onTogglePin,
    onToggleFeature,
    onToggleFeaturedBrandPin,
    onRemoveFromLibrary,
    statusInfo,
}) => {
    const [openDeleteConfirmation, setOpenDeleteConfirmation] =
        React.useState(false);
    const [
        openRemoveFromLibraryConfirmation,
        setOpenRemoveFromLibraryConfirmation,
    ] = React.useState(false);

    const buildMenuItems = () => {
        const actionItems: ContextMenuItem[] = [];

        if (onEdit) {
            actionItems.push({
                type: 'item',
                render: () => <span>Edit</span>,
                onClick: onEdit,
            });
        }

        if (onRepositionImage) {
            actionItems.push({
                type: 'item',
                render: () => <span>Reposition Image</span>,
                onClick: onRepositionImage,
            });
        }

        if (onTogglePin) {
            actionItems.push({
                type: 'item',
                render: () =>
                    statusInfo?.pinned ? (
                        <span>Unpin From Homepage</span>
                    ) : (
                        <span>Pin To Homepage</span>
                    ),
                onClick: onTogglePin,
            });
        }

        if (onToggleFeature) {
            actionItems.push({
                type: 'item',
                render: () =>
                    statusInfo?.featured ? (
                        <span>Unpin from Top of Directory</span>
                    ) : (
                        <span>Pin to Top of Directory</span>
                    ),
                onClick: onToggleFeature,
            });
        }

        if (onToggleFeaturedBrandPin) {
            actionItems.push({
                type: 'item',
                render: () =>
                    statusInfo?.featuredBrandPinned ? (
                        <span>Unpin From Featured Brands</span>
                    ) : (
                        <span>Pin To Featured Brands</span>
                    ),
                onClick: onToggleFeaturedBrandPin,
            });
        }

        if (onRemoveFromLibrary) {
            actionItems.push({
                type: 'item',
                render: () => <span>Remove From Library</span>,
                onClick: () => setOpenRemoveFromLibraryConfirmation(true),
            });
        }

        if (onDelete) {
            actionItems.push({
                type: 'separator',
            });
            actionItems.push({
                type: 'item',
                render: () => <span>Delete</span>,
                onClick: () => setOpenDeleteConfirmation(true),
            });
        }

        const infoSection: ContextMenuItem[] = [];

        if (statusInfo?.created) {
            infoSection.push({
                type: 'label',
                render: () => (
                    <TileAdminTimestampInfo
                        action="created"
                        userFullName={statusInfo.created.createdBy}
                        date={statusInfo.created.createdAt}
                    />
                ),
            });
        }

        if (
            statusInfo?.approved?.approvedAt &&
            statusInfo?.approved?.approvedAt
        ) {
            infoSection.push({
                type: 'label',
                render: () => (
                    <TileAdminTimestampInfo
                        action="approved"
                        userFullName={statusInfo?.approved?.approvedBy ?? ''}
                        date={statusInfo?.approved?.approvedAt ?? ''}
                    />
                ),
            });
        }

        if (
            statusInfo?.published?.publishedAt &&
            statusInfo?.published?.publishedBy
        ) {
            infoSection.push({
                type: 'label',
                render: () => (
                    <TileAdminTimestampInfo
                        action="published"
                        userFullName={statusInfo?.published?.publishedBy ?? ''}
                        date={statusInfo?.published?.publishedAt ?? ''}
                    />
                ),
            });
        }

        if (statusInfo?.kpi) {
            infoSection.push({
                type: 'label',
                render: () =>
                    statusInfo.kpi && (
                        <TileKPIContextMenu kpi={statusInfo.kpi} />
                    ),
            });
        }

        if (infoSection.length > 0) {
            actionItems.push({
                type: 'separator',
            });
            actionItems.push(...infoSection);
        }

        return actionItems;
    };

    return (
        <>
            <ContextMenu menuItems={buildMenuItems()}>{children}</ContextMenu>
            {onDelete && (
                <AlertDialog
                    title="Are you absolutely sure?"
                    description="This action cannot be undone. This will permanently delete this product and its associated information and tags."
                    onConfirm={onDelete}
                    controlled={{
                        open: openDeleteConfirmation,
                        setOpen: setOpenDeleteConfirmation,
                    }}
                />
            )}
            {onRemoveFromLibrary && (
                <AlertDialog
                    title="Remove from library?"
                    description="The item will be hidden from the library but kept in the CMS so it can be restored later."
                    onConfirm={onRemoveFromLibrary}
                    controlled={{
                        open: openRemoveFromLibraryConfirmation,
                        setOpen: setOpenRemoveFromLibraryConfirmation,
                    }}
                />
            )}
        </>
    );
};
